import axios from 'axios';

/**
 * AUT-223 — turning a capture / reconcile rejection into something the
 * operator can act on.
 *
 * These error codes are not noise. `payment_not_capturable` means refresh;
 * `payment_capture_already_claimed` means the money already moved and a second
 * attempt would be a mistake; `payment_capture_failed` means the next step is
 * reconcile, NOT another capture. Flattening all four into "что-то пошло не
 * так" would delete exactly the information the operator opened this drawer to
 * get.
 *
 * Unknown codes fall back to the SERVER's own message rather than a generic
 * one — a developer-English string an operator can paste into a bug report
 * beats a polite Russian nothing. Only a rejection with no message at all
 * (network drop, CORS) gets the generic key.
 */

/**
 * AUT-301 — the one capture rejection whose copy depends on its `details`, so
 * it does not map to a flat `error.<code>` key like the others.
 */
export const HOLD_WINDOW_CLOSED_CODE = 'payment_hold_window_closed';

/** Rejections `POST /admin/payments/:id/capture` can emit. */
export const CAPTURE_ERROR_CODES: ReadonlySet<string> = new Set([
  'payment_not_capturable',
  'payment_capture_already_claimed',
  'payment_capture_failed',
  HOLD_WINDOW_CLOSED_CODE,
]);

/** Rejections `POST /admin/payments/:id/reconcile-capture` can emit. */
export const RECONCILE_ERROR_CODES: ReadonlySet<string> = new Set(['payment_not_reconcilable']);

/**
 * Either an i18n key to translate, or a verbatim string the server supplied.
 * The caller resolves — keeping `t` out of this module makes it a pure unit.
 */
export type CaptureErrorText =
  | { kind: 'key'; key: string; params?: Record<string, string> }
  | { kind: 'raw'; text: string };

const GENERIC_KEY = 'orders.capture.error.generic';

/** The `{code, message, details?}` envelope every `DomainError` serialises to. */
interface DomainErrorBody {
  code?: unknown;
  message?: unknown;
  details?: unknown;
}

const HOLD_WINDOW_KEY_BASE = `orders.capture.error.${HOLD_WINDOW_CLOSED_CODE}`;

/**
 * AUT-301 — `409 payment_hold_window_closed`, and why it needs two sentences.
 *
 * The server reaches this only past `holdExpiresAt`, and only after ASKING the
 * provider whether the hold survived. `details.providerStatus` carries the
 * answer, and `null` means there was no answer — the provider could not be
 * reached. Those are different facts with different next actions: "провайдер
 * говорит, что холда нет" ends the matter (new payment or cancel the order),
 * while "мы не смогли спросить" is an infrastructure problem and the money may
 * well still be blocked. Collapsing them into one "холд истёк" sends an
 * operator to re-charge a customer whose card is still holding the funds, or to
 * chase a provider outage that never happened.
 *
 * `holdExpiresAt` from `details` is deliberately NOT quoted here: the hold row
 * directly above already shows it, both absolutely and relatively, and a `null`
 * there would need its own em-dash phrasing for no gain.
 */
const resolveHoldWindowClosed = (details: unknown): CaptureErrorText => {
  const providerStatus =
    typeof details === 'object' && details !== null
      ? (details as { providerStatus?: unknown }).providerStatus
      : undefined;
  const reported = typeof providerStatus === 'string' ? providerStatus.trim() : '';
  return reported.length > 0
    ? {
        kind: 'key',
        key: `${HOLD_WINDOW_KEY_BASE}.providerReported`,
        params: { providerStatus: reported },
      }
    : { kind: 'key', key: `${HOLD_WINDOW_KEY_BASE}.providerUnreachable` };
};

export const resolveCaptureError = (err: unknown, known: ReadonlySet<string>): CaptureErrorText => {
  if (!axios.isAxiosError(err)) return { kind: 'key', key: GENERIC_KEY };

  const body = err.response?.data as DomainErrorBody | undefined;
  const code = typeof body?.code === 'string' ? body.code : null;
  // Checked before the flat mapping: this code's key is a NAMESPACE, so
  // `orders.capture.error.payment_hold_window_closed` would resolve to an
  // object and render as the raw key.
  if (code === HOLD_WINDOW_CLOSED_CODE && known.has(code)) {
    return resolveHoldWindowClosed(body?.details);
  }
  if (code && known.has(code)) return { kind: 'key', key: `orders.capture.error.${code}` };

  const message = typeof body?.message === 'string' ? body.message.trim() : '';
  if (message.length > 0) return { kind: 'raw', text: message };

  return { kind: 'key', key: GENERIC_KEY };
};
